import React, { useEffect, useState } from 'react';
import { Button, Spin, Typography } from 'antd';

const { Text } = Typography;

const MAX_AUTO_RETRIES = 3;

interface Props {
  src: string;
  alt: string;
  height: number | string;
  background: string;
  border?: string;
  /** Short label shown when the image could not be produced (e.g. the word type). */
  fallbackLabel?: string;
  compact?: boolean;
  /**
   * Show the whole picture in its own proportions instead of cropping it to `height` (which is
   * then only the size while it loads or when it failed).
   */
  natural?: boolean;
}

/** The proportions of the generated pictures (512x352), until a picture says otherwise. */
const PICTURE_RATIO = 512 / 352;

function withRetryParam(src: string, attempt: number): string {
  if (attempt === 0) return src;
  return `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`;
}

/**
 * AI images are generated on demand and can take a while or fail, so this shows a loading state,
 * quietly retries a few times, and finally falls back to a labelled placeholder with a Retry button
 * instead of a broken-image icon.
 */
export const CardImage: React.FC<Props> = ({
  src,
  alt,
  height,
  background,
  border,
  fallbackLabel,
  compact,
  natural,
}) => {
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [ratio, setRatio] = useState(PICTURE_RATIO);

  useEffect(() => {
    setAttempt(0);
    setStatus('loading');
  }, [src]);

  const handleError = () => {
    if (attempt < MAX_AUTO_RETRIES) {
      setTimeout(() => setAttempt((a) => a + 1), 2500 * (attempt + 1));
    } else {
      setStatus('failed');
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        ...(natural && status !== 'failed' ? { aspectRatio: String(ratio) } : { height }),
        background,
        border,
      }}
    >
      {status !== 'failed' && (
        <img
          key={attempt}
          src={withRetryParam(src, attempt)}
          alt={alt}
          onLoad={(event) => {
            const img = event.currentTarget;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              setRatio(img.naturalWidth / img.naturalHeight);
            }
            setStatus('loaded');
          }}
          onError={handleError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: status === 'loaded' ? 'block' : 'none',
          }}
        />
      )}

      {status === 'loading' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Spin size={compact ? 'small' : 'medium'} />
          {!compact && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {attempt > 0 ? 'Still generating the image…' : 'Generating image…'}
            </Text>
          )}
        </div>
      )}

      {status === 'failed' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {!compact && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fallbackLabel ? `${fallbackLabel} · ` : ''}image unavailable
            </Text>
          )}
          <Button
            size="small"
            onClick={() => {
              setAttempt(0);
              setStatus('loading');
            }}
          >
            Retry
          </Button>
        </div>
      )}
    </div>
  );
};
